// Routenplaner: Typen, Standardwerte und Hilfen; ohne Node-Abhängigkeiten (auch im Browser nutzbar).
import type { PlaceType } from "./travel-shared";
import { WEATHER_REGION_KEYS, WEATHER_REGIONS, weatherCode, type ForecastDay, type WeatherRegion } from "./weather-shared";

export const TRAVEL_MODES = { auto: "Auto", bicycle: "Fahrrad", pedestrian: "Zu Fuß" } as const;
export type TravelMode = keyof typeof TRAVEL_MODES;
export const TRAVEL_MODE_KEYS = Object.keys(TRAVEL_MODES) as [TravelMode, ...TravelMode[]];

/** Standard-Verweildauer in Minuten je Ortstyp; pro Ort im Admin überschreibbar. */
export const DWELL_DEFAULTS: Partial<Record<PlaceType, number>> = {
  beach: 120, bar: 90, club: 90, restaurant: 75, hotel: 0, viewpoint: 20,
};
export const DWELL_OTHER = 45;
export const dwellFor = (type: string, own: number | null | undefined) => own ?? DWELL_DEFAULTS[type as PlaceType] ?? DWELL_OTHER;

/** Ein Ort, den der Planer als Stopp anbieten darf. */
export type PlannerPlace = {
  id: number; slug: string; name: string; type: string; lat: number; lng: number; dwell: number; hours: boolean;
  /** Nächstgelegene der drei Vorhersagezonen. Grobe Schublade, kein Mikroklima. */
  zone: WeatherRegion;
  visited: boolean;
};

export type RoutePoint = { lat: number; lng: number };
/** Etappe; from/to sind Indizes in die angefragten Stopps, -1 ist der Startpunkt. */
export type RouteLeg = { from: number; to: number; seconds: number; meters: number; winding: boolean };
export type RouteResult = {
  /** Reihenfolge der Stopps als Indizes in die angefragte Liste */
  order: number[];
  legs: RouteLeg[];
  /** Fahrzeit inklusive Zuschlag */
  seconds: number;
  meters: number;
  /** Summe der Verweildauern */
  dwell: number;
  /** Fahrzeit plus Verweildauer */
  day: number;
  /** Kodierte Polyline je Etappe, Präzision 6 */
  shape: string[];
  factor: number;
};
export type RouteError = { error: string };

/** Zustand im Browser, im Teilen-Link und in localStorage. */
export type PlannerState = {
  start: (RoutePoint & { placeId?: number; label?: string }) | null;
  stops: { id: number; dwell?: number }[];
  mode: TravelMode;
  optimize: boolean;
  back: boolean;
};

export const EMPTY_PLAN: PlannerState = { start: null, stops: [], mode: "auto", optimize: false, back: false };
export const PLAN_KEY = "gcg-route";
/** Abendplaner unter /lgbt. Getrennt, damit ein Seitenwechsel die allgemeine Route nicht überschreibt. */
export const PLAN_KEY_LGBT = "gcg-route-lgbt";
export const PLAN_EVENT = "gcg-route";

/** Dekodiert eine Polyline (Valhalla: Präzision 6) zu [lat, lng]. */
export function decodePolyline(s: string, precision = 6): [number, number][] {
  const f = 10 ** precision;
  const out: [number, number][] = [];
  let i = 0, lat = 0, lng = 0;
  while (i < s.length) {
    for (const k of [0, 1]) {
      let shift = 0, res = 0, b: number;
      do {
        b = s.charCodeAt(i++) - 63;
        res |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20 && i < s.length);
      const d = res & 1 ? ~(res >> 1) : res >> 1;
      if (k === 0) lat += d; else lng += d;
    }
    out.push([lat / f, lng / f]);
  }
  return out;
}

export function fmtDuration(sec: number) {
  const min = Math.max(1, Math.round(sec / 60));
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60), m = min % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

export const fmtDistance = (m: number) => (m < 1000 ? `${Math.round(m / 10) * 10} m` : `${(m / 1000).toLocaleString("de-DE", { maximumFractionDigits: m < 10000 ? 1 : 0 })} km`);

/** Teilen-Link: Zustand kompakt im Fragment, damit beim Aufruf nichts an den Server geht. */
export function encodePlan(p: PlannerState) {
  const q = new URLSearchParams();
  if (p.start) q.set("s", p.start.placeId ? `p${p.start.placeId}` : `${p.start.lat.toFixed(5)},${p.start.lng.toFixed(5)}`);
  if (p.stops.length) q.set("o", p.stops.map((s) => (s.dwell != null ? `${s.id}~${s.dwell}` : String(s.id))).join("."));
  if (p.mode !== "auto") q.set("m", p.mode);
  if (p.optimize) q.set("opt", "1");
  if (p.back) q.set("z", "1");
  return `route=1&${q.toString()}`;
}

export function decodePlan(hash: string, places: PlannerPlace[]): PlannerState | null {
  const q = new URLSearchParams(hash.replace(/^#/, ""));
  if (q.get("route") !== "1") return null;
  const byId = new Map(places.map((p) => [p.id, p]));
  const plan: PlannerState = { ...EMPTY_PLAN, stops: [] };
  const s = q.get("s");
  if (s?.startsWith("p")) {
    const p = byId.get(Number(s.slice(1)));
    if (p) plan.start = { lat: p.lat, lng: p.lng, placeId: p.id, label: p.name };
  } else if (s) {
    const [lat, lng] = s.split(",").map(Number);
    if (Number.isFinite(lat) && Number.isFinite(lng)) plan.start = { lat, lng };
  }
  for (const part of (q.get("o") ?? "").split(".").filter(Boolean)) {
    const [id, dwell] = part.split("~").map(Number);
    if (byId.has(id) && !plan.stops.some((x) => x.id === id)) plan.stops.push({ id, ...(Number.isFinite(dwell) && dwell >= 0 && dwell <= 720 ? { dwell } : {}) });
  }
  const m = q.get("m");
  if (m && m in TRAVEL_MODES) plan.mode = m as TravelMode;
  plan.optimize = q.get("opt") === "1";
  plan.back = q.get("z") === "1";
  return plan;
}

export function savePlan(p: PlannerState, key = PLAN_KEY) {
  try {
    localStorage.setItem(key, JSON.stringify(p));
  } catch {}
  window.dispatchEvent(new Event(PLAN_EVENT));
}

/** Interessen für den Tagesstart. Hotel, Bar, Club und Veranstaltung bleiben draußen. */
export const DAY_INTERESTS = ["beach", "viewpoint", "nature", "village", "sight"] as const;
export const DAY_HOURS = [4, 6, 8] as const;
export const SUGGEST_EMPTY = "Dafür liegen noch zu wenige geprüfte Orte vor.";

const SPEED_KMH: Record<TravelMode, number> = { auto: 45, bicycle: 15, pedestrian: 4 };

function kmBetween(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(h));
}

/** Zone eines Orts: der näher liegende der drei Vorhersagepunkte. */
export function placeZone(lat: number, lng: number): WeatherRegion {
  let best: WeatherRegion = "sueden";
  let near = Infinity;
  for (const key of WEATHER_REGION_KEYS) {
    const d = kmBetween({ lat, lng }, WEATHER_REGIONS[key]);
    if (d < near) {
      near = d;
      best = key;
    }
  }
  return best;
}

export type DaySuggestion = { start: PlannerState["start"]; stops: { id: number }[]; note: string };

/**
 * Stopps für einen Tag, nur aus der gewählten Zone und den Interessen.
 * Fahrzeit ist die Luftlinie. Es wird nichts an die Routing-Engine geschickt.
 */
export function suggestDay(input: {
  places: PlannerPlace[];
  base: WeatherRegion;
  hours: number;
  interests: readonly string[];
  mode: TravelMode;
  maxStops: number;
  start: PlannerState["start"];
}): DaySuggestion {
  const empty: DaySuggestion = { start: input.start, stops: [], note: SUGGEST_EMPTY };
  const speed = SPEED_KMH[input.mode];
  const inZone = input.places.filter((p) => p.zone === input.base);
  const pool = inZone.filter((p) => input.interests.includes(p.type));
  let start = input.start;
  let from = start ? { lat: start.lat, lng: start.lng } : null;
  const blocked = new Set<number>();
  if (!start) {
    const anchor = WEATHER_REGIONS[input.base];
    const nearest = [...inZone].sort((a, b) => kmBetween(anchor, a) - kmBetween(anchor, b))[0];
    if (!nearest) return empty;
    start = { lat: nearest.lat, lng: nearest.lng, placeId: nearest.id, label: nearest.name };
    from = nearest;
    blocked.add(nearest.id);
  } else if (start.placeId) blocked.add(start.placeId);
  if (!from) return empty;

  const budget = input.hours * 60 * 0.75;
  const stops: { id: number }[] = [];
  let spent = 0;
  let cursor = from;
  const left = () => pool.filter((p) => !blocked.has(p.id) && !stops.some((s) => s.id === p.id));
  while (stops.length < input.maxStops) {
    const ranked = left().sort((a, b) => Number(b.visited) - Number(a.visited) || kmBetween(cursor, a) - kmBetween(cursor, b));
    let pick: PlannerPlace | null = null;
    let cost = 0;
    for (const p of ranked) {
      const need = (kmBetween(cursor, p) / speed) * 60 + p.dwell;
      if (spent + need <= budget) {
        pick = p;
        cost = need;
        break;
      }
    }
    if (!pick) break;
    stops.push({ id: pick.id });
    spent += cost;
    cursor = pick;
  }
  return stops.length ? { start, stops, note: "" } : empty;
}

/** Heutiges Datum auf den Kanaren, YYYY-MM-DD. */
export function canaryToday(now = new Date()) {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Atlantic/Canary", year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
}

export type TodayWeather = Partial<Record<WeatherRegion, ForecastDay>>;

function sky(code: number) {
  const w = weatherCode(code);
  if (w.icon === "sun" || w.icon === "partly") return "Sonne";
  return w.label;
}

/** Hinweis, wenn Küste und Tejeda heute auseinanderliegen. */
export function coastMountainHint(base: WeatherRegion, stopZones: WeatherRegion[], days: TodayWeather): string | null {
  const mountain = days.berge;
  const coastKey: WeatherRegion = base === "berge" ? (stopZones.includes("norden") ? "norden" : "sueden") : base;
  const coast = days[coastKey];
  if (!mountain || !coast || !Number.isFinite(mountain.tmax) || !Number.isFinite(coast.tmax)) return null;
  const wet = (c: number) => c >= 51;
  const dry = (c: number) => c <= 3;
  const diverges = Math.abs(coast.tmax - mountain.tmax) >= 6
    || (wet(coast.code) && dry(mountain.code))
    || (wet(mountain.code) && dry(coast.code))
    || mountain.wind >= 40;
  if (!diverges) return null;
  const t = (n: number) => Math.round(n);
  return `In Tejeda heute ${t(mountain.tmax)} °C und ${sky(mountain.code)}, an der Küste ${t(coast.tmax)} °C und ${sky(coast.code)}. Für die Bergstrecke mehr Zeit und eine Jacke einplanen.`;
}
